import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Gets user's country using IP-based geolocation (no permission required)
 * @returns {Promise<string|null>} - Returns country name or null if unavailable
 */
export async function getUserCountry() {
  try {
    // Use a free IP geolocation service
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Check if we got valid country data
    if (data.country_name) {
      return data.country_name;
    } else {
      return null;
    }
  } catch (error) {
    // Fallback to browser language detection
    return getCountryFromLanguage();
  }
}

/**
 * Fallback: Get country hint from browser language/locale
 * @returns {string|null} - Returns country name or null
 */
function getCountryFromLanguage() {
  try {
    const language = navigator.language || navigator.userLanguage;
    if (!language) return null;

    // Map common language codes to countries (very basic mapping)
    const languageToCountry = {
      'en-US': 'United States',
      'en-GB': 'United Kingdom',
      'en-CA': 'Canada',
      'en-AU': 'Australia',
      'fr-FR': 'France',
      'de-DE': 'Germany',
      'es-ES': 'Spain',
      'it-IT': 'Italy',
      'pt-BR': 'Brazil',
      'ja-JP': 'Japan',
      'ko-KR': 'South Korea',
      'zh-CN': 'China',
      'zh-TW': 'Taiwan',
      'ru-RU': 'Russia',
      'ar-SA': 'Saudi Arabia',
      'hi-IN': 'India'
    };

    const country = languageToCountry[language];
    if (country) {
      return country;
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Parses userAgent string to extract Operating System, Browser, and Device Type
 * @returns {Object} - Returns { operatingSystem, browser, deviceType }
 */
function parseUserAgent() {
  const userAgent = navigator.userAgent;
  let operatingSystem = 'Unknown';
  let browser = 'Unknown';
  let deviceType = 'desktop'; // Default to desktop

  // Operating System detection - check mobile first
  if (userAgent.includes('Android')) operatingSystem = 'Android';
  else if (userAgent.includes('iPhone') || userAgent.includes('iPad') || userAgent.includes('iPod')) operatingSystem = 'iOS';
  else if (userAgent.includes('Windows NT 10.0')) operatingSystem = 'Windows 10/11';
  else if (userAgent.includes('Windows NT 6.3')) operatingSystem = 'Windows 8.1';
  else if (userAgent.includes('Windows NT 6.2')) operatingSystem = 'Windows 8';
  else if (userAgent.includes('Windows NT 6.1')) operatingSystem = 'Windows 7';
  else if (userAgent.includes('Windows NT 6.0')) operatingSystem = 'Windows Vista';
  else if (userAgent.includes('Windows NT 5.1')) operatingSystem = 'Windows XP';
  else if (userAgent.includes('Windows')) operatingSystem = 'Windows';
  else if (userAgent.includes('Mac OS X')) operatingSystem = 'macOS';
  else if (userAgent.includes('Linux') && !userAgent.includes('Android')) operatingSystem = 'Linux';
  else if (userAgent.includes('CrOS')) operatingSystem = 'Chrome OS';

  // Device Type detection (mobile vs desktop)
  // Check for tablets first
  if (userAgent.includes('iPad')) {
    deviceType = 'tablet';
  } else if (userAgent.includes('Tablet')) {
    deviceType = 'tablet';
  } else if (userAgent.includes('Android') && !userAgent.includes('Mobile')) {
    // Android tablets don't have "Mobile" in their user agent
    deviceType = 'tablet';
  } else if (userAgent.includes('iPhone') || userAgent.includes('iPod')) {
    deviceType = 'mobile';
  } else if (userAgent.includes('Mobile') && !userAgent.includes('Tablet')) {
    deviceType = 'mobile';
  } else if (userAgent.includes('BlackBerry') || userAgent.includes('BB10')) {
    deviceType = 'mobile';
  } else if (userAgent.includes('Windows Phone') || userAgent.includes('IEMobile')) {
    deviceType = 'mobile';
  } else if (userAgent.includes('Opera Mini') || userAgent.includes('Opera Mobi')) {
    deviceType = 'mobile';
  } else {
    // Additional check for screen size (if available)
    // if (typeof window !== 'undefined' && window.screen) {
    //   const screenWidth = window.screen.width;
    //   const screenHeight = window.screen.height;
    //   const minDimension = Math.min(screenWidth, screenHeight);

      // If the smallest screen dimension is less than 768px, likely mobile/tablet
    //   if (minDimension < 768) {
    //     deviceType = minDimension < 480 ? 'mobile' : 'tablet';
    //   }
    // }

    // Check for touch capability as additional indicator
    if (typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
      // If we have touch capability and small screen, likely mobile/tablet
      if (typeof window !== 'undefined' && window.screen && Math.min(window.screen.width, window.screen.height) < 768) {
        deviceType = 'mobile';
      }
    }
  }

  // Browser detection (check in order of specificity)
  if (userAgent.includes('CriOS')) browser = 'Google Chrome'; // Chrome on iOS
  else if (userAgent.includes('FxiOS')) browser = 'Mozilla Firefox'; // Firefox on iOS
  else if (userAgent.includes('Edg/')) browser = 'Microsoft Edge';
  else if (userAgent.includes('Chrome') && !userAgent.includes('Chromium')) browser = 'Google Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Mozilla Firefox';
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('CriOS')) browser = 'Apple Safari';
  else if (userAgent.includes('Opera') || userAgent.includes('OPR/')) browser = 'Opera';
  else if (userAgent.includes('Chromium')) browser = 'Chromium';
  else if (userAgent.includes('Trident') || userAgent.includes('MSIE')) browser = 'Internet Explorer';

  return { operatingSystem, browser, deviceType };
}

/**
 * Creates a new user submission document and tracks game start
 * @param {number} gameNumber - Current game number
 * @param {string|null} docId - Optional: Existing document ID for subsequent games
 * @param {string} language - User's selected language ('en' or 'es')
 * @returns {Promise<string>} - Returns the document ID
 */
export async function trackGameStart(gameNumber, docId = null, language = 'es') {
  try {
    // Get country data when game starts (no permission required)
    const country = await getUserCountry();
    
    // Parse user agent for OS and browser info
    const { operatingSystem, browser, deviceType } = parseUserAgent();
    
    const gameStartField = `game_${gameNumber}_start`;
    const gameData = {
      [gameStartField]: serverTimestamp(),
      operatingSystem: operatingSystem,
      browser: browser,
      deviceType: deviceType,
      language: language
    };
    
    // Include country data in the initial document
    if (country) {
      gameData.country = country;
    }
    
    if (docId) {
      // Update existing document for subsequent games
      const docRef = doc(db, 'user_submissions', docId);
      await updateDoc(docRef, gameData);
      return docId;
    } else {
      // Create new document with auto-generated ID for first game
      gameData.createdAt = serverTimestamp();
      const docRef = await addDoc(collection(db, 'user_submissions'), gameData);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error tracking game start:', error);
    throw error;
  }
}

/**
 * Tracks game end and completion data
 * @param {string} docId - The Firebase document ID
 * @param {Object} userData - The user data object
 * @param {string} userData.userName - User's name
 * @param {string} userData.userEmail - User's email
 * @param {string} userData.questionAnswer - User's answer to the question
 * @param {number} userData.level - Level reached in the game
 * @param {number} userData.misses - Number of misses
 * @param {number} userData.gameNumber - Current game number
 * @param {number} userData.sessionScore - Cumulative score for entire session
 * @param {number} userData.sessionMisses - Cumulative misses for entire session
 * @param {boolean} userData.privacyConsent - User's privacy policy consent
 * @returns {Promise<void>}
 */
export async function submitUserData(docId, userData) {
  try {
    const docRef = doc(db, 'user_submissions', docId);
    const gameNumber = userData.gameNumber;
    
    const gameData = {
      name: userData.userName,
      email: userData.userEmail,
      answer: userData.questionAnswer,
      [`game_${gameNumber}_score`]: userData.level,
      [`game_${gameNumber}_misses`]: userData.misses,
      [`game_${gameNumber}_end`]: serverTimestamp(),
      session_score: userData.sessionScore,
      session_misses: userData.sessionMisses,
      lastUpdated: serverTimestamp()
    };
    
    // Add privacy consent only if provided (only recorded once)
    if (userData.privacyConsent !== undefined) {
      gameData.privacyConsent = userData.privacyConsent;
      gameData.privacyConsentTimestamp = serverTimestamp();
    }
    
    // Update existing document
    await updateDoc(docRef, gameData);
  } catch (error) {
    console.error('Error submitting user data:', error);
    throw error;
  }
}

/**
 * Tracks store visit event
 * @param {string} docId - The Firebase document ID
 * @param {number} gameNumber - Current game number
 * @returns {Promise<void>}
 */
export async function trackStoreVisit(docId, gameNumber) {
  try {
    const docRef = doc(db, 'user_submissions', docId);
    const storeVisitField = `game_${gameNumber}_storeVisit`;
    
    await updateDoc(docRef, {
      [storeVisitField]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error tracking store visit:', error);
    throw error;
  }
}

/**
 * Tracks code copied event
 * @param {string} docId - The Firebase document ID
 * @param {number} gameNumber - Current game number
 * @returns {Promise<void>}
 */
export async function trackCodeCopied(docId, gameNumber) {
  try {
    const docRef = doc(db, 'user_submissions', docId);
    const codeCopiedField = `game_${gameNumber}_codeCopied`;
    
    await updateDoc(docRef, {
      [codeCopiedField]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error tracking code copied:', error);
    throw error;
  }
}

/**
 * Tracks badge download event
 * @param {string} docId - The Firebase document ID
 * @param {number} gameNumber - Current game number
 * @returns {Promise<void>}
 */
export async function trackBadgeDownload(docId, gameNumber) {
  try {
    const docRef = doc(db, 'user_submissions', docId);
    const badgeDownloadField = `game_${gameNumber}_badgeDownloaded`;
    
    await updateDoc(docRef, {
      [badgeDownloadField]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error tracking badge download:', error);
    throw error;
  }
}

/**
 * Tracks level completion when user clicks "Next Level"
 * @param {string} docId - The Firebase document ID
 * @param {number} gameNumber - Current game number
 * @param {number} level - Level that was just completed
 * @param {number} misses - Number of misses for this level
 * @param {number} sessionScore - Cumulative score across all games in session
 * @param {number} sessionMisses - Cumulative misses across all games in session
 * @returns {Promise<void>}
 */
export async function trackLevelCompletion(docId, gameNumber, level, misses, sessionScore, sessionMisses) {
  try {
    const docRef = doc(db, 'user_submissions', docId);
    
    await updateDoc(docRef, {
      [`game_${gameNumber}_score`]: level,
      [`game_${gameNumber}_misses`]: misses,
      session_score: sessionScore,
      session_misses: sessionMisses,
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error tracking level completion:', error);
    throw error;
  }
}